import {countSourcesForGroup, isAllLocationsGroupKey} from './reviews-link-action-rules';

export const ALL_LOCATIONS_SOURCE_VALUE = 'all';

const normalizeMode = (mode) => String(mode || 'group').trim().toLowerCase();

const normalizeOptions = (options = []) => {
    if (!Array.isArray(options)) {
        return [];
    }

    return options
        .map((option) => {
            const value = String(option?.value ?? '').trim();
            const label = String(option?.label ?? option?.value ?? '').trim();
            return {
                value,
                label: label || value
            };
        })
        .filter(option => !!option.value);
};

export const getSourceOptions = (sourceOptions = []) => normalizeOptions(sourceOptions);

const getCustomGroupOptions = (groupOptions = []) => {
    return normalizeOptions(groupOptions)
        .filter(option => !isAllLocationsGroupKey(option.value));
};

const getAllLocationsLabel = (groupOptions = []) => {
    const normalized = normalizeOptions(groupOptions);
    const all = normalized.find(option => isAllLocationsGroupKey(option.value));
    return all?.label || 'All locations';
};

export const doesClientAddSelectableOptions = ({
    websiteGroupOptions = [],
    websiteSourceOptions = [],
    clientGroupOptions = [],
    clientSourceOptions = []
} = {}) => {
    const toIdentitySet = (groupOptions, sourceOptions) => {
        const identities = new Set();

        getCustomGroupOptions(groupOptions)
            .forEach(option => identities.add(`group:${option.value.toLowerCase()}`));

        getSourceOptions(sourceOptions)
            .forEach(option => identities.add(`source:${option.value.toLowerCase()}`));

        return identities;
    };

    const websiteSet = toIdentitySet(websiteGroupOptions, websiteSourceOptions);
    const clientSet = toIdentitySet(clientGroupOptions, clientSourceOptions);

    return Array.from(clientSet).some(identity => !websiteSet.has(identity));
};

export const buildReviewSourceOptions = ({groupOptions = [], sourceOptions = []} = {}) => {
    const allOption = {
        value: ALL_LOCATIONS_SOURCE_VALUE,
        label: getAllLocationsLabel(groupOptions)
    };

    const groupSelectionOptions = getCustomGroupOptions(groupOptions)
        .map(option => ({
            value: `group:${option.value}`,
            label: option.label
        }));

    const locationSelectionOptions = getSourceOptions(sourceOptions)
        .map(option => ({
            value: `source:${option.value}`,
            label: option.label
        }));

    const validValues = new Set([
        allOption.value,
        ...groupSelectionOptions.map(option => option.value),
        ...locationSelectionOptions.map(option => option.value)
    ]);

    return {
        allOption,
        groupSelectionOptions,
        locationSelectionOptions,
        isValidValue: (value) => validValues.has(String(value || '').trim())
    };
};

export const toReviewSourceValue = (config = {}) => {
    const sourceKey = String(config.sourceKey || '').trim();
    if (sourceKey) {
        return `source:${sourceKey}`;
    }

    const mode = normalizeMode(config.mode);
    if (mode === 'single') {
        return '';
    }

    const groupKey = String(config.groupKey || '').trim();
    if (!groupKey || isAllLocationsGroupKey(groupKey)) {
        return ALL_LOCATIONS_SOURCE_VALUE;
    }

    return `group:${groupKey}`;
};

export const toConfigPatch = (sourceValue) => {
    const normalized = String(sourceValue || '').trim();

    if (normalized.startsWith('source:')) {
        return {
            mode: 'single',
            sourceKey: normalized.slice('source:'.length),
            groupKey: ALL_LOCATIONS_SOURCE_VALUE
        };
    }

    if (normalized.startsWith('group:')) {
        return {
            mode: 'group',
            groupKey: normalized.slice('group:'.length),
            sourceKey: ''
        };
    }

    return {
        mode: 'group',
        groupKey: ALL_LOCATIONS_SOURCE_VALUE,
        sourceKey: ''
    };
};

export const isSimpleSingleLocationState = ({
    scope,
    groupOptions = [],
    sourceOptions = []
} = {}) => {
    if ((scope || 'website') !== 'website') {
        return false;
    }

    if (getCustomGroupOptions(groupOptions).length > 0) {
        return false;
    }

    return getSourceOptions(sourceOptions).length === 1;
};

export const resolveSelectionCount = ({config = {}, sourceOptions = []} = {}) => {
    const sourceKey = String(config.sourceKey || '').trim();
    if (sourceKey) {
        return 1;
    }

    const groupKey = String(config.groupKey || '').trim();
    if (!groupKey) {
        return null;
    }

    return countSourcesForGroup(sourceOptions, groupKey);
};
